// @fileoverview This is a composable for the photo album editor.
// It is used to handle the photo album editor's state and actions.
// It is also used to handle the photo album editor's content picker sources and search modes.

import { __ } from '@@/bits/intl'
import { getAttachmentsFromDraft } from '@@/bits/post_drafts'
import { isImageUrl } from '@@/bits/url'
import { SnackbarNotificationType } from '@@/enums'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePanelsStore } from '@@/pinia/surface_panels'
import type { Cid, PostAttributes } from '@@/types'
import useLogger from '@@/vuecomposables/logger'
import { usePostAttachmentUploader } from '@@/vuecomposables/usePostAttachmentUploader'
import { createSharedComposable } from '@vueuse/core'
import { ref } from 'vue'

const PLACEHOLDER_UPLOADING_ATTACHMENT = { url: '', progress: 0, isUploadingFile: true }
const logger = useLogger('photo-album-editor', { logLifecycleHooks: false, log: true })

export const usePhotoAlbumEditor = createSharedComposable(() => {
  const contentPickerStore = useContentPickerStore()
  const surfaceContentPickerStore = useSurfaceContentPickerStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const currentItemIndex = ref(0)

  const updatePhotoAlbumAttachmentIndex = (index: number): void => {
    currentItemIndex.value = index
  }

  // #region Photo album image picker and photo editor dialog visibility
  const surfacePanelsStore = useSurfacePanelsStore()
  const showPhotoAlbumImagePicker = (): void => {
    surfaceContentPickerStore.hidePostContentPicker()
    surfacePanelsStore.xPhotoAlbumImagePicker = true
    contentPickerStore.showContentSourceMenu({ contentPickerContext: 'photo-album-image-picker' })
  }

  const hidePhotoAlbumImagePicker = (): void => {
    surfacePanelsStore.xPhotoAlbumImagePicker = false
    contentPickerStore.hideContentSourceMenu()

    showPhotoAlbumEditorDialog()
  }

  const showPhotoAlbumEditorDialog = (): void => {
    surfacePanelsStore.xPhotoAlbumEditorDialog = true
  }

  const hidePhotoAlbumEditorDialog = (): void => {
    surfacePanelsStore.xPhotoAlbumEditorDialog = false
  }
  // #endregion

  // #region Image upload logic
  const surfaceStore = useSurfaceStore()
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const { startUploadFile, stopUploadFile } = usePostAttachmentUploader()

  const getDraftAndCid = (postCid?: Cid): { draft: PostAttributes | null; cid?: Cid } => {
    if (postCid == null) {
      return { draft: surfaceDraftsStore.activeDraft, cid: surfaceDraftsStore.activeDraft?.cid }
    }
    return { draft: surfaceDraftsStore.getDraftFromCid(postCid), cid: postCid }
  }

  /**
   * Sets placeholder attachments in a draft up to the specified index
   * Used to show upload progress states while files are being uploaded
   * @param draft - The post draft to add placeholder attachments to
   * @param file - The file being uploaded. If provided, it will be set as the file being uploaded for the last placeholder attachment.
   */
  const setPlaceholderAttachments = (draft: PostAttributes, file?: File): void => {
    const existingAttachments = getAttachmentsFromDraft(draft)
    const targetIndex = existingAttachments.length
    const attachments = [...existingAttachments]
    while (attachments.length <= targetIndex) {
      attachments.push(PLACEHOLDER_UPLOADING_ATTACHMENT)
    }
    if (file != null) {
      attachments[attachments.length - 1].fileBeingUploaded = file
    }

    surfaceDraftsStore.updateDraft({
      attachments,
      cid: draft.cid,
    })
  }

  const onUploadStart = ({
    file,
    url, // for Google Drive. unnecessary for now.
    postCid,
    index,
  }: {
    file?: File
    url?: string
    postCid?: Cid
    index?: number
  }): void => {
    logger.log('onUploadStart', { file, url, postCid, index })

    const { draft, cid } = getDraftAndCid(postCid)
    if (draft == null || cid == null || file == null) return

    const attachments = getAttachmentsFromDraft(draft)
    setPlaceholderAttachments(draft, file)

    // TODO: use custom max file upload size if given
    const maxFileUploadSize = surfaceStore.uploadLimit
    startUploadFile({ cid, file, attachmentIndex: index ?? attachments.length, maxFileUploadSize })
  }

  const onUploadProgress = ({
    progress,
    postCid,
    index,
  }: {
    progress: number
    postCid?: Cid
    index?: number
  }): void => {
    logger.log('onUploadProgress', { progress, postCid, index })
    const { draft, cid } = getDraftAndCid(postCid)
    if (draft == null || cid == null) return
    const attachments = getAttachmentsFromDraft(draft)
    attachments[index ?? attachments.length].progress = progress
    surfaceDraftsStore.updateDraft({ attachments, cid })
  }

  const onUploadCancel = (postCid?: Cid, index?: number): void => {
    logger.log('onUploadCancel', { postCid, index })
    const { draft, cid } = getDraftAndCid(postCid)
    if (draft == null || cid == null) return
    const attachments = getAttachmentsFromDraft(draft)
    stopUploadFile({ cid, attachmentIndex: index ?? attachments.length })
    const updatedAttachments = [...attachments]
    updatedAttachments.splice(index ?? attachments.length, 1)
    surfaceDraftsStore.updateDraft({
      attachments: updatedAttachments,
      cid,
    })
  }

  const onUploadDone = (url: string, payload?: { altText?: string; postCid?: Cid }): void => {
    logger.log('onUploadDone', { url, payload })
    const { draft, cid } = getDraftAndCid(payload?.postCid)
    if (draft == null || cid == null) return
    const attachments = [...getAttachmentsFromDraft(draft), { url, alt: payload?.altText }]

    // discard progress states of attachments
    surfaceDraftsStore.updateDraft({
      attachments: attachments.map((a) => ({
        id: a.id,
        url: a.url,
        alt: a.alt,
        signed_url: a.signed_url,
        hotlink: a.hotlink,
      })),
      cid,
    })
    currentItemIndex.value = attachments.length - 1
  }

  const onUploadError = (errorSource?: File | any): void => {
    logger.error('onUploadError', { errorSource })
  }

  const selectFile = ({ file }: { file: File }): void => {
    logger.log('selectFile', { file })
    onUploadStart({ file })
  }

  const selectLink = async (url: string, payload: { altText?: string }): Promise<void> => {
    logger.log('selectLink', { url })
    const { draft, cid } = getDraftAndCid()
    if (draft == null || cid == null) return
    const attachments = getAttachmentsFromDraft(draft)
    if (attachments.length === 0) {
      surfaceDraftsStore.updateDraft({
        attachments: [{ url, alt: payload.altText }],
        attachment: url,
        wish_content: {
          attachment_props: { altText: payload.altText },
          is_processed: false,
        },
        cid,
      })
    } else {
      onUploadDone(url, payload)
    }

    const isValid = await isImageUrl(url)
    if (!isValid) {
      globalSnackbarStore.setSnackbar({
        message: __('Invalid image URL'),
        notificationType: SnackbarNotificationType.error,
      })
      // revert the attachment to the previous state
      const updatedAttachments = [...attachments]
      updatedAttachments.splice(updatedAttachments.length - 1, 1)
      surfaceDraftsStore.updateDraft({
        attachments: updatedAttachments,
        cid,
      })
    }
  }

  const selectFiles = ({ files }: { files: File[] }): void => {
    const { draft, cid } = getDraftAndCid()
    if (draft == null || cid == null) return
    logger.log('selectFiles', { files, draft, cid })

    const attachments = getAttachmentsFromDraft(draft)
    files.forEach((file, index) => {
      onUploadStart({ file, index: index + attachments.length, postCid: cid })
    })
  }
  // #endregion

  return {
    showPhotoAlbumImagePicker,
    hidePhotoAlbumImagePicker,
    showPhotoAlbumEditorDialog,
    hidePhotoAlbumEditorDialog,

    onUploadStart,
    onUploadProgress,
    onUploadDone,
    onUploadCancel,
    onUploadError,
    selectFile,
    selectFiles,
    selectLink,

    currentItemIndex,
    updatePhotoAlbumAttachmentIndex,
  }
})
